import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { apiFetch } from "../api/client.js";
import { toast } from "../lib/toast.js";

interface Props {
  tosVersion: string;
  privacyVersion: string;
  onAccepted: () => void;
  children: ComponentChildren;
}

export function LegalInterstitial({
  tosVersion,
  privacyVersion,
  onAccepted,
}: Props) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleAccept() {
    setSubmitting(true);
    try {
      const res = await apiFetch("/v1/account/accept-terms", {
        method: "POST",
        body: JSON.stringify({
          tos_version: tosVersion,
          privacy_version: privacyVersion,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error?.message ?? "Failed to accept terms");
      }

      toast.success("Terms accepted");
      onAccepted();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to accept terms",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 p-4">
      <div class="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 class="mb-4 text-lg font-bold text-gray-900">
          Updated Terms &amp; Privacy Policy
        </h2>
        <p class="mb-4 text-sm text-gray-700">
          We've updated our Terms of Service and Privacy Policy. Please review
          them before continuing.
        </p>
        <ul class="mb-6 space-y-2 text-sm">
          <li>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 underline hover:text-blue-800"
            >
              Terms of Service ({tosVersion})
            </a>
          </li>
          <li>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 underline hover:text-blue-800"
            >
              Privacy Policy ({privacyVersion})
            </a>
          </li>
        </ul>
        <label class="mb-4 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) =>
              setChecked((e.target as HTMLInputElement).checked)
            }
            class="mt-0.5"
          />
          <span>
            I have read and agree to the Terms of Service ({tosVersion}) and
            Privacy Policy ({privacyVersion})
          </span>
        </label>
        <button
          type="button"
          disabled={!checked || submitting}
          onClick={handleAccept}
          class="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Accepting…" : "Accept and Continue"}
        </button>
      </div>
    </div>
  );
}
